import React, { useContext, useEffect, useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import { bbox } from "@turf/turf";
import { confirmAlert } from "react-confirm-alert";
import { MapActions, MapContext } from "context/map";
import { getCropTypes } from "api/Field";
import Confirm from "components/Confirm";
import useClearPopUps from "hooks/useClearPopUps";
import ManageToolbar from "../ManageToolbar";
import ManageAction from "../ManageAction";
import ManageFieldDrawer from "../ManageFieldDrawer";

const ManageFarm = () => {
  const location = useLocation();
  const [previousField, setPreviousField] = useState(null);
  const [saveSwitchField, setSaveSwitchField] = useState(false);
  const { state, dispatch } = useContext(MapContext);
  const {
    mode,
    map,
    draw,
    currentField,
    manageStatus,
    drawerOpen,
    cropTypes,
    fields,
    selectedFields,
    measureMode,
  } = state;

  const clearPopUps = useClearPopUps();

  const processField = useCallback(
    (event) => {
      const featureId = event.features[0].id;
      if (!measureMode) {
        if (!currentField) {
          dispatch({ type: MapActions.SET_CURRENT_FIELD, value: featureId });
          setPreviousField(featureId);
        }
      }
      clearPopUps();
    },
    [currentField, dispatch, measureMode, clearPopUps]
  );

  // const removeDrawLayer = useCallback(async () => {
  //   const mapboxDrawLayers = map
  //     .getStyle()
  //     .layers?.filter((x) => x?.id.includes("gl-draw-"));

  //   if (mapboxDrawLayers?.length) {
  //     mapboxDrawLayers.forEach((element) => {
  //       // console.log("element", element);
  //       if (map.getLayer(element?.id)) {
  //         map.removeLayer(element?.id);
  //       }
  //     });
  //   }
  //   // console.log("mapboxDrawLayers", mapboxDrawLayers);
  //   if (map.getSource("mapbox-gl-draw-cold")) {
  //     console.log("remove cold");
  //     map.removeSource("mapbox-gl-draw-cold");
  //   }
  //   if (map.getSource("mapbox-gl-draw-hot")) {
  //     console.log("remove hot");
  //     map.removeSource("mapbox-gl-draw-hot");
  //   }
  // }, [map]);

  const initializeDraw = useCallback(async () => {
    map.on("draw.create", processField);
  }, [map, processField]);

  const fetchCropTypes = useCallback(async () => {
    const cropTypesData = await getCropTypes();

    dispatch({ type: MapActions.SET_CROP_TYPES, value: cropTypesData });
  }, [dispatch]);

  const confirmSwitchField = useCallback(() => {
    const options = {
      title: "Are you sure?",
      message: "You are switching to other field.",
      buttons: [
        {
          label: "Save changes and continue",
          onClick: () => {
            setSaveSwitchField(true);
          },
          className: "-success",
        },
        {
          label: "Cancel",
          onClick: () => {
            if (currentField) {
              dispatch({
                type: MapActions.SWITCH_FIELD,
              });
            } else {
              dispatch({
                type: MapActions.SWITCH_FIELD,
                value: previousField,
              });
            }
          },
          lassName: "",
        },
      ],
      childrenElement: () => <div />,
      customUI: ({ title, message, buttons, onClose }) => (
        <Confirm
          title={title}
          message={message}
          buttons={buttons}
          onClose={onClose}
        />
      ),
      closeOnEscape: false,
      closeOnClickOutside: false,
      overlayClassName: "overlay-custom-class-name",
    };

    confirmAlert(options);
  }, [currentField, dispatch, previousField]);

  const removeDrawState = useCallback(() => {
    if (
      location &&
      location.state?.draw &&
      manageStatus === "create-boundaries"
    ) {
      location.state = {};
      window.history.replaceState({}, document.title);
    }
  }, [location, manageStatus]);

  const editSelectedField = useCallback(() => {
    if (!draw) return;

    if (location?.state?.draw && selectedFields.length > 0) {
      dispatch({ type: MapActions.REMOVE_SELECTED_FIELDS });

      return;
    }

    if (previousField && previousField !== selectedFields[0]) {
      confirmSwitchField();

      return;
    }

    const feature = fields.find((item) => item.id === selectedFields[0]);

    if (map.getLayer(`fill-${feature.id}`)) {
      map.removeLayer(`fill-${feature.id}`);
    }

    if (map.getLayer(`outline-${feature.id}`)) {
      map.removeLayer(`outline-${feature.id}`);
    }

    if (map.getSource(`source-${feature.id}`)) {
      map.removeSource(`source-${feature.id}`);
    }

    draw.add(feature);
    draw.changeMode("direct_select", { featureId: feature.id });

    dispatch({ type: MapActions.EDIT_SELECTED_FIELD });

    setPreviousField(feature.id);
  }, [
    confirmSwitchField,
    dispatch,
    draw,
    fields,
    location?.state?.draw,
    map,
    previousField,
    selectedFields,
  ]);

  const editCurrentField = useCallback(() => {
    if (!draw) return;

    if (previousField === currentField) {
      draw.changeMode("direct_select", { featureId: currentField });
    }
  }, [currentField, draw, previousField]);

  useEffect(() => {
    if (!map) return;
    if (mode === "manage") {
      initializeDraw();
    }
  }, [initializeDraw, map, mode]);

  useEffect(() => {
    if (cropTypes && !cropTypes.length && mode !== "view") {
      fetchCropTypes();
    }
  }, [cropTypes, fetchCropTypes, mode]);

  useEffect(() => {
    if (manageStatus === "create" && currentField) {
      const fieldFeature = draw.get(currentField);
      const padding = {
        left: 0,
        top: 100,
        right: drawerOpen ? 360 : 0,
        bottom: 150,
      };
      const fieldBbox = bbox(fieldFeature);

      map.fitBounds(fieldBbox, {
        padding,
      });
    }
  }, [currentField, draw, drawerOpen, manageStatus, map]);

  useEffect(() => {
    if (selectedFields.length && mode === "manage") {
      editSelectedField();
    }
    removeDrawState();
  }, [editSelectedField, mode, removeDrawState, selectedFields.length]);

  useEffect(() => {
    if (selectedFields && currentField && mode === "manage") {
      editCurrentField();
    }
  }, [currentField, editCurrentField, mode, selectedFields]);

  useEffect(() => {
    if (!selectedFields.length && !currentField) {
      setPreviousField(null);
    }
  }, [currentField, selectedFields.length]);

  useEffect(() => {
    if (mode === "view") {
      setPreviousField(null);
    }
  }, [mode]);

  if (mode === "view") return null;

  return (
    <>
      <ManageToolbar />
      <ManageAction />
      <ManageFieldDrawer
        previousField={previousField}
        setPreviousField={setPreviousField}
        saveSwitchField={saveSwitchField}
        setSaveSwitchField={setSaveSwitchField}
      />
    </>
  );
};

export default ManageFarm;
